import { Chat } from '@ai-sdk/react';
import {
  type CrowdexUIMessage,
  crowdexChatDataPartSchemas,
  crowdexChatMessageMetadataSchema,
  crowdexInternalToolInputSchemas,
  isCrowdexInternalToolName,
} from '@crowdex/agent-contracts';
import type { ChatInit } from 'ai';

export type CrowdexClientToolExecutor = ((
  name: string,
  input: Record<string, unknown>,
) => Promise<unknown>) & { handles?: (name: string) => boolean };
export type CrowdexClientToolResultSubmitter = (result: {
  toolCallId: string;
  output?: unknown;
  errorText?: string;
}) => Promise<void>;

/** Interactions resume the active ACP prompt directly; never create a duplicate AI SDK turn. */
export function shouldAutoSubmitCrowdexChat(): boolean {
  return false;
}

/** The single Chat construction path shared by primary, split, and side conversations. */
export function createCrowdexChat(
  options: Omit<
    ChatInit<CrowdexUIMessage>,
    'dataPartSchemas' | 'messageMetadataSchema' | 'onToolCall' | 'sendAutomaticallyWhen'
  > & {
    executeClientTool?: CrowdexClientToolExecutor;
    submitClientToolResult?: CrowdexClientToolResultSubmitter;
  },
): Chat<CrowdexUIMessage> {
  const { executeClientTool, submitClientToolResult, ...chatOptions } = options;
  const reportToolOutputError = (error: unknown) =>
    chatOptions.onError?.(error instanceof Error ? error : new Error(String(error)));
  let chat: Chat<CrowdexUIMessage>;
  chat = new Chat<CrowdexUIMessage>({
    ...chatOptions,
    // Keep browser and PostgreSQL identities aligned across latest-message
    // continuations, approvals, edits, and stream reconnection.
    generateId: chatOptions.generateId ?? (() => crypto.randomUUID()),
    dataPartSchemas: crowdexChatDataPartSchemas,
    messageMetadataSchema: crowdexChatMessageMetadataSchema,
    ...(executeClientTool === undefined
      ? {}
      : {
          onToolCall: async ({ toolCall }) => {
            // Server-executed ACP/MCP tools also pass through the UI
            // stream. Only Crowdex's deliberately client-executed tools must
            // be answered with Chat.addToolOutput here.
            if (
              !isCrowdexInternalToolName(toolCall.toolName) &&
              !executeClientTool.handles?.(toolCall.toolName)
            )
              return;
            let result: { output?: unknown; errorText?: string };
            try {
              const input = isCrowdexInternalToolName(toolCall.toolName)
                ? crowdexInternalToolInputSchemas[toolCall.toolName].parse(toolCall.input)
                : (toolCall.input as Record<string, unknown>);
              const output = await executeClientTool(toolCall.toolName, input);
              result = { output: output ?? null };
            } catch (error) {
              const errorText = error instanceof Error ? error.message : String(error);
              result = { errorText };
            }
            // The runtime is authoritative. Keep the UI retryable until the
            // blocked ACP turn has acknowledged this interaction.
            await submitClientToolResult?.({ toolCallId: toolCall.toolCallId, ...result });
            // onToolCall runs inside the SDK's serial update queue.
            // addToolOutput queues another update, so awaiting it here deadlocks
            // the stream. Enqueue it and let this callback release the queue.
            if (result.errorText === undefined) {
              void Promise.resolve(
                chat.addToolOutput({
                  // AI SDK's static tool-key type also backs dynamic tool parts at runtime.
                  tool: toolCall.toolName as keyof typeof crowdexInternalToolInputSchemas,
                  toolCallId: toolCall.toolCallId,
                  output: result.output,
                }),
              ).catch(reportToolOutputError);
            } else {
              void Promise.resolve(
                chat.addToolOutput({
                  state: 'output-error',
                  // AI SDK's static tool-key type also backs dynamic tool parts at runtime.
                  tool: toolCall.toolName as keyof typeof crowdexInternalToolInputSchemas,
                  toolCallId: toolCall.toolCallId,
                  errorText: result.errorText,
                }),
              ).catch(reportToolOutputError);
            }
          },
        }),
    sendAutomaticallyWhen: shouldAutoSubmitCrowdexChat,
  });
  return chat;
}
